import { ArrowBack, ArrowDropDown, CloseRounded, Launch } from '@mui/icons-material';
import { Box, Button, CircularProgress, Container, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import EquipmentCard from './EquipmentCard';
import ServiceDetailsCard from './ServiceDetailsCard';
import MapCard from './MapCard';
import { i18n } from '../../../translate/i18n';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from "../../../store/actions";
import { RootState } from '../../../store/types';
import Checklist from './Checklist';
import { RouteComponentProps } from 'react-router-dom';
import Spinner from '../../../components/Spinner';
import confirm from '../../../UI/Confirm/confirm';
import moment from 'moment';
import JSZip from 'jszip';
import { IFile } from '../../../UI/FileList';
import FolderZipRoundedIcon from '@mui/icons-material/FolderZipRounded';
import { IPhoto } from '../../../models/iphoto';

interface RouteParams {
  id: string
}

interface IDownloadReportRequestBody {
  url: string,
  language: string
}

const EditService: React.FC<RouteComponentProps<RouteParams>> = (props) => {
  const { history } = props;
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loadingFilesZip, setLoadingFilesZip] = useState<boolean>(false);

  const selectedServiceId = useSelector((state: RootState) => state.editService.selectedServiceId);
  const serviceLoading = useSelector((state: RootState) => state.editService.serviceLoading);
  const reportLoading = useSelector((state: RootState) => state.editService.reportLoading);
  const selectedAccess = useSelector((state: any) => state.auth.selectedAccess);
  const userId = useSelector((state: any) => state.auth.id);
  const selectedService = useSelector((state: RootState) => state.editService.selectedService);
  
  const isAdmin = selectedAccess.role === 'admin';
  const isAnalyst = selectedAccess.role === 'analyst';
  const daysPastSinceServiceCreation = moment().diff(moment(selectedService.createdAt), 'days');
  const daysAllowedToAnalystUpdateService = 3;
  
  const getShouldAllowUpdate = () => {
    if (isAdmin) return true;
    if (isAnalyst && (daysPastSinceServiceCreation <= daysAllowedToAnalystUpdateService) && (selectedService.inspectedBy._id === userId)) return true;
    return false;
  }
  const shouldAllowUpdate = getShouldAllowUpdate();

  const goBackAndClearState = () => {
    history.goBack()
    dispatch(actions.editServiceClearState());
  }

  const goToActionPage = (actionId: string) => {
    props.history.push(`/form-action/${actionId}`)
  }

  useEffect(() => {
    dispatch(actions.editServiceSelectService(props.match.params.id))
  }, [])

  const fetchService = useCallback(
    () => dispatch(actions.editServiceFetchServiceById(selectedServiceId)),
    [selectedServiceId],
  );

  useEffect(() => {
    selectedServiceId && fetchService();
  }, [selectedServiceId])

  const downloadReport = () => dispatch(actions.editServiceDownloadReport(props.match.params.id));
  const downloadExport = () => dispatch(actions.editServiceDownloadExport(props.match.params.id))

  const deleteServices = (): any => dispatch(actions.deleteServices(props.match.params.id));

  const handleDeleteService = () => {
    confirm(i18n.t('checkDeleteCompanyCallFormService.confirm'),
      {
        title: i18n.t('checkDeleteCompanyCallFormService.title'),
        titleColor: 'red',
        disableBackdrop: true
      })
      .then(
        () => {
          console.log('rejeitado');
        },
        async () => {
          console.log('confirmado');
          deleteServices().then((res: any) => {
            console.log('res', res);
            history.goBack();
          })
        },
      )
  }

  const goToEquipmentPage = () => {
    props.history.push(`/form-equip/${selectedService.equipment._id}/form-edit-service/${props.match.params.id}`);
  }

  const goToPlacePage = () => {
    props.history.push(`/admin-equip-places/${selectedService.place._id}`);
  }

  const serviceTitle: string = `${selectedService.equipment.equipmentModel.name} (${selectedService.equipmentService.serviceType.name} ${selectedService.equipmentService.frequency.name})`

  const getFileAndPutOnFolder = async (file: IFile | IPhoto, folder: JSZip) => {
    const downloadedFile = await fetch(file.url!, {mode: 'cors', cache: 'no-cache'});
    const blob = await downloadedFile.blob();
    folder?.file(file.name, blob);
  }

  const downloadFilesZip = async () => {
    const zip = new JSZip();
    const equipmentFolder = zip.folder('equipamento');
    const questionsFolder = zip.folder('questões');
    const signatureFolder = zip.folder('assinaturas');
    const attachmentsFolder = zip.folder('anexos');

    const questionsPromises = selectedService.questions.map(async (question) => {
      if (question.files && question.files.length > 0) {
        const questionFolder = questionsFolder?.folder(question.name + '-' + question.questionsId);
        const filesPromises = question.files.map(async (file) => {
          await getFileAndPutOnFolder(file, questionFolder!);
        })
        await Promise.all(filesPromises);
      }
    });

    await Promise.all(questionsPromises);
    
    const attachments = selectedService.files;
    const equipmentFile = selectedService.equipment.photo;

    const signaturesToDownload = selectedService?.signatures && selectedService.signatures.length > 0 ? selectedService.signatures : (selectedService?.signature?.file && selectedService?.signature.file.url !== '') ? [selectedService?.signature] : [];

    if (signaturesToDownload.length > 0) {
      const signaturesPromises = signaturesToDownload.map(async (signatureFile) => {
        const file = await fetch(signatureFile.file.url!, {mode: 'cors', cache: 'no-cache'});
        const blob = await file.blob();
        const fileName = signatureFile.name.includes('.png') ? signatureFile.name : `${signatureFile.name}.png`;
        const complement = signatureFile.complement || signatureFile.document; 
        const fileNameWithNameAndComplement = signatureFile.name + '-' + complement + '-' + fileName;
        signatureFolder?.file(fileNameWithNameAndComplement, blob);
      })
      await Promise.all(signaturesPromises);
    }
    if (equipmentFile) {
      await getFileAndPutOnFolder(equipmentFile, equipmentFolder!);
    }
    if (attachments.length > 0) {
      const attachmentsPromises = attachments.map(async (file) => {
        await getFileAndPutOnFolder(file, attachmentsFolder!);
      });
      await Promise.all(attachmentsPromises);
    }

    const zipFile = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(zipFile);
    link.download = `${selectedService.equipmentService.serviceType.name} ${selectedService.equipmentService.frequency.name}-${selectedService.equipment.equipmentModel.name}-${new Date(selectedService.date).toLocaleDateString('pt-BR')}.zip`;
    link.click();

    setLoadingFilesZip(false);
  }

  return(
    <>
      {serviceLoading && <Spinner overlay size={30} />}
      <Box sx={{display: 'flex'}}>

      <IconButton onClick={goBackAndClearState} sx={sxs.arrowBack} size="large"><ArrowBack /></IconButton>
      <Container>
        <Typography sx={sxs.title} variant='h5'>{serviceLoading ? '' : serviceTitle}</Typography>
        <Container sx={sxs.buttonsContainer}>
          <Button
            color='primary'
            variant='contained'
            sx={sxs.printerButton}
            disabled={reportLoading}
            id='buttonExport'
            aria-controls={Boolean(anchorEl) ? 'menuOptions' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {reportLoading ?
              <CircularProgress size={16} style={{ color: 'green' }} /> :
                <>
                  <Typography>{i18n.t('editService.buttonReport') as string}</Typography>
                  <ArrowDropDown />
                </>}
          </Button>
          <Menu
            id="menuOptions"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              'aria-labelledby': 'buttonExport',
            }}
          >
            <MenuItem onClick={() => { downloadReport(); setAnchorEl(null); }} sx={sxs.menuItem}>
              <PrintRoundedIcon />
              <Typography>{i18n.t('editService.reportMenuItem') as string}</Typography>
            </MenuItem>
            <MenuItem onClick={() => { downloadExport(); setAnchorEl(null); }} sx={sxs.menuItem}>
              <Launch />
              <Typography>{i18n.t('editService.exportMenuItem') as string}</Typography>
            </MenuItem>
            <MenuItem onClick={() => { setLoadingFilesZip(true); downloadFilesZip(); }} sx={sxs.menuItem}>
              <Box display={'flex'}>
                { loadingFilesZip ? <CircularProgress size={16} style={{ color: 'green' }} /> : <FolderZipRoundedIcon /> }
                <Typography sx={{marginLeft: '8px'}}>{i18n.t('editService.downloadFiles') as string}</Typography>
              </Box>
            </MenuItem>
          </Menu>
          <IconButton children={editMode ? <CloseRounded/> : <EditRounded/>} disabled={!shouldAllowUpdate} onClick={() => setEditMode(!editMode)}/>
          <IconButton children={<DeleteRounded/>} disabled={!shouldAllowUpdate} onClick={handleDeleteService}/>
        </Container>
        <EquipmentCard goToEquipmentPage={goToEquipmentPage} goToPlacePage={goToPlacePage}/>
        <Box sx={{...sxs.cardsContainer, opacity: serviceLoading ? '0.3' : '1'}}>
          <ServiceDetailsCard editMode={editMode}/>
          <MapCard />
        </Box>
        <Checklist goBack={goBackAndClearState} editMode={editMode} setEditMode={setEditMode} goToActionPage={goToActionPage}/>
      </Container>
      </Box>
    </>
  )
}
const sxs = {
  arrowBack: {
    alignSelf: 'flex-start',
  },
  divContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginLeft: '30px',
    marginBottom: '24px',
  },
  container: {
    padding: '10px',
    margin: 0,
    position: 'relative'
  },
  title: {
    marginTop: '10px',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    margin: '24px',
  },
  printerButton: {
    display: 'flex',
    gap: 1,
    width: '14rem'
  },
  cardsContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 3,
    justifyConten: 'space-between',
    width: '100%',
    marginBottom: '20px',
  },
  menuItem: {
    width: '14rem',
    display: 'flex',
    gap: 1,
  }
}

export default EditService;
